import React, { useEffect, useState } from 'react';

import {
    AdjustmentModel,
    ContractTermsEnum,
    GetAdjustmentsInput,
    ThresholdOperatorEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isEmpty, isUndefined } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { AppContext } from 'src/contexts/appContext';
import { IContractTermsComponentProps } from 'src/types/IRateCard';

const DEFAULT_THRESHOLD_OPERATOR = ThresholdOperatorEnum.GREATER_THAN;

export const SubscriberVolumeDiscount = ({
    contractTerms,
    isEditMode,
    handleContractTermValueChange
}: IContractTermsComponentProps) => {
    const [subscriberVolumeDiscountAdjustmentModel, setSubscriberVolumeDiscountAdjustmentModel] =
        useState<AdjustmentModel>({});
    const { setTopAlertMsg } = React.useContext(AppContext);

    const getSubscriberVolumeDiscountAdjustmentModelDefValue = (): { [p: string]: any } | undefined => {
        const definitions: { [key: string]: any } = {};
        subscriberVolumeDiscountAdjustmentModel.adjustmentModelDefinitions?.forEach((definition) => {
            if (!isUndefined(definition.type)) {
                definitions[definition.type] = definition.value;
            }
        });
        return !isEmpty(definitions) ? definitions : undefined;
    };

    const handleHasSubscriberVolumeDiscountChange = (newVal: boolean) => {
        const definitions = getSubscriberVolumeDiscountAdjustmentModelDefValue();
        if (isUndefined(definitions) || isEmpty(definitions)) return;
        handleContractTermValueChange(
            'subscriberVolumeDiscount',
            'threshold',
            newVal ? definitions[ContractTermsEnum.SUBSCRIBER_THRESHOLD] : undefined
        );
        handleContractTermValueChange(
            'subscriberVolumeDiscount',
            'thresholdOperator',
            newVal ? DEFAULT_THRESHOLD_OPERATOR : undefined
        );
        handleContractTermValueChange(
            'subscriberVolumeDiscount',
            'ratesReducedByFraction',
            newVal ? definitions[ContractTermsEnum.DISCOUNT_FRACTION] : undefined
        );
    };

    useEffect(() => {
        if (isEditMode) {
            contractConfigurationClient
                .getAdjustments(
                    GetAdjustmentsInput.clone({
                        adjustmentModel: ContractTermsEnum.SUBSCRIBER_VOLUME_BASED_DISCOUNT
                    })
                )
                .toPromise()
                .then((response) => {
                    if (!isEmpty(response.adjustmentModels)) {
                        setSubscriberVolumeDiscountAdjustmentModel(response.adjustmentModels?.[0] ?? {});
                    }
                })
                .catch((error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('GetAdjustments API Unexpected error: ', error.receivedException?.message);
                    setTopAlertMsg(error.receivedException?.message);
                });
        }
    }, []);

    return (
        <Box className="box-border-light-gray" type="outline" spacingInset="400">
            <Column>
                <Row spacing="100" alignmentHorizontal="justify">
                    <Row spacing="none">
                        <Heading level={3}>Subscriber volume discount</Heading>
                        <ContractTermInfoButton contractTermName={ContractTermName.SubscriberVolumeDiscount} />
                    </Row>
                    {isEditMode && (
                        <Toggle
                            label={''}
                            value={!isUndefined(contractTerms.subscriberVolumeDiscount?.threshold)}
                            onChange={handleHasSubscriberVolumeDiscountChange}
                            isEditMode={isEditMode}
                            disabled={isUndefined(getSubscriberVolumeDiscountAdjustmentModelDefValue())}
                        />
                    )}
                </Row>
                {isUndefined(contractTerms.subscriberVolumeDiscount?.threshold) ? (
                    <Row>
                        <Toggle label="Enable Subscriber Volume Based discount" value={false} isEditMode={false} />
                    </Row>
                ) : (
                    <Row alignmentVertical="top" widths="30%">
                        <Column spacing="100">
                            <Text type="b200">Threshold</Text>
                            <Text type="b300">
                                {Number(contractTerms.subscriberVolumeDiscount?.threshold).toLocaleString()}
                            </Text>
                        </Column>
                        <Column spacing="100">
                            <Text type="b200">Threshold Operator</Text>
                            <Text type="b300">{contractTerms.subscriberVolumeDiscount?.thresholdOperator}</Text>
                        </Column>
                        <Column spacing="100">
                            <Text type="b200"> Rates reduced by fraction </Text>
                            <Text type="b300">{contractTerms.subscriberVolumeDiscount?.ratesReducedByFraction}</Text>
                        </Column>
                    </Row>
                )}
            </Column>
        </Box>
    );
};
